/**
 * SiteGpsPickerModal - Leaflet map modal for setting site GPS coordinates
 *
 * Accessible to ALL authenticated users (not admin-gated).
 * Uses same Leaflet stack as MapWrapper/SitesView.
 */
import React, { useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { X, MapPin, Crosshair, Save, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [48.8566, 2.3522]; // Paris
const DEFAULT_ZOOM = 6;
const PLACED_ZOOM = 15;

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function FlyToPosition({ position, zoom }) {
  const map = useMap();
  React.useEffect(() => {
    if (position) {
      map.flyTo(position, zoom, { duration: 0.8 });
    }
  }, [position, zoom, map]);
  return null;
}

function SiteGpsPickerModal({ site, onSave, onClose }) {
  const { t } = useTranslation();
  const hasExistingGps = site.gps_latitude && site.gps_longitude &&
    !isNaN(parseFloat(site.gps_latitude)) && !isNaN(parseFloat(site.gps_longitude));

  const [lat, setLat] = useState(hasExistingGps ? parseFloat(site.gps_latitude) : null);
  const [lng, setLng] = useState(hasExistingGps ? parseFloat(site.gps_longitude) : null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyZoom, setFlyZoom] = useState(null);
  const [geolocating, setGeolocating] = useState(false);

  const initialCenter = hasExistingGps
    ? [parseFloat(site.gps_latitude), parseFloat(site.gps_longitude)]
    : DEFAULT_CENTER;
  const initialZoom = hasExistingGps ? PLACED_ZOOM : DEFAULT_ZOOM;

  const handleMapClick = useCallback((newLat, newLng) => {
    setLat(Math.round(newLat * 1000000) / 1000000);
    setLng(Math.round(newLng * 1000000) / 1000000);
    setError(null);
  }, []);

  const handleGeolocate = useCallback(() => {
    if (!navigator.geolocation) {
      setError(t('sites.gps.errors.unsupported'));
      return;
    }
    setGeolocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newLat = Math.round(pos.coords.latitude * 1000000) / 1000000;
        const newLng = Math.round(pos.coords.longitude * 1000000) / 1000000;
        setLat(newLat);
        setLng(newLng);
        setFlyTarget([newLat, newLng]);
        setFlyZoom(PLACED_ZOOM);
        setGeolocating(false);
        setError(null);
      },
      (err) => {
        setGeolocating(false);
        setError(t('sites.gps.errors.positionFailed', { message: err.message }));
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [t]);

  const handleSave = async () => {
    if (lat === null || lng === null) {
      setError(t('sites.gps.clickHint'));
      return;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setError(t('sites.gps.errors.invalidCoordinates'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(site.id, lat, lng);
      onClose();
    } catch (err) {
      setError(err.message || t('sites.errors.saveFailed'));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-purple-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6" />
            <div>
              <h2 className="text-lg font-bold">
                {hasExistingGps ? t('sites.gps.editTitle') : t('sites.gps.setTitle')}
              </h2>
              <p className="text-purple-200 text-sm">{site.site_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-purple-200 p-1">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Map */}
        <div className="relative" style={{ height: '400px' }}>
          <MapContainer
            center={initialCenter}
            zoom={initialZoom}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapClickHandler onMapClick={handleMapClick} />
            {flyTarget && <FlyToPosition position={flyTarget} zoom={flyZoom} />}
            {lat !== null && lng !== null && (
              <Marker position={[lat, lng]} />
            )}
          </MapContainer>

          {/* Geolocation button */}
          <button
            onClick={handleGeolocate}
            disabled={geolocating}
            className="absolute top-4 right-4 z-[1000] px-3 py-2 bg-white rounded-lg shadow-md hover:bg-gray-50 flex items-center gap-2 text-sm font-medium text-gray-700 disabled:opacity-50"
          >
            {geolocating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Crosshair className="w-4 h-4" />
            )}
            {t('sites.gps.myPosition')}
          </button>

          {/* Click hint */}
          {lat === null && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] px-4 py-2 bg-black bg-opacity-70 text-white text-sm rounded-lg">
              {t('sites.gps.clickHint')}
            </div>
          )}
        </div>

        {/* Coordinates display + actions */}
        <div className="px-6 py-4 border-t border-gray-200">
          {/* Coordinates */}
          <div className="flex items-center gap-6 mb-4">
            <div className="flex-1">
              <label className="text-xs text-gray-500 uppercase">{t('sites.gps.latitude')}</label>
              <p className="font-mono text-sm font-medium text-gray-900">
                {lat !== null ? lat : '-'}
              </p>
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500 uppercase">{t('sites.gps.longitude')}</label>
              <p className="font-mono text-sm font-medium text-gray-900">
                {lng !== null ? lng : '-'}
              </p>
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600 mb-3">{error}</p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || lat === null}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {t('common.actions.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SiteGpsPickerModal;
